import { useMemo } from 'react';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import {
  type ConnectorManifest,
  type FolderKind,
  type ImportTarget,
  applyFeatureFlagConnectorFilters,
  listConnectorsFor,
  listConnectorsForTarget,
} from '../connectorRegistry';
import { ensureDefaultConnectorsRegistered } from '../registerDefaultConnectors';

export interface UseConnectorListOptions {
  folderKind: FolderKind;
  diagramType?: string;
  /** When provided, narrows to connectors that can write to this sink. */
  target?: ImportTarget;
}

export interface ConnectorListState {
  connectors: ConnectorManifest[];
  isLoading: boolean;
}

/**
 * Memoized synchronous read of the ConnectorRegistry.
 *
 * The registry is populated synchronously at bootstrap so a hook is enough —
 * no TanStack Query needed. Re-runs only when the kind/diagramType/target
 * changes. Hidden connectors are always excluded.
 */
export function useConnectorListState(options: UseConnectorListOptions): ConnectorListState {
  ensureDefaultConnectorsRegistered();
  const { folderKind, diagramType, target } = options;
  const {
    showProcessExtractionUx,
    isBlueprintEnabled,
    isRdmImportExportEnabled,
    loading,
  } = useFeatureFlags();
  const baseConnectors = useMemo(
    () =>
      target
        ? listConnectorsForTarget(folderKind, target, diagramType)
        : listConnectorsFor(folderKind, diagramType),
    [folderKind, diagramType, target],
  );
  const connectors = useMemo(() => {
    if (loading) {
      return [];
    }
    return applyFeatureFlagConnectorFilters(baseConnectors, {
      folderKind,
      showProcessExtractionUx,
      blueprintEnabled: isBlueprintEnabled(),
      rdmImportExportEnabled: isRdmImportExportEnabled(),
    });
  }, [
    baseConnectors,
    folderKind,
    isBlueprintEnabled,
    isRdmImportExportEnabled,
    loading,
    showProcessExtractionUx,
  ]);
  return { connectors, isLoading: loading };
}

export function useConnectorList(options: UseConnectorListOptions): ConnectorManifest[] {
  return useConnectorListState(options).connectors;
}
